import { useCallback, useMemo } from "react";

import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import { LightbulbIcon } from "lucide-react";

import { pluralise } from "simplydo/core/helpers";
import { InvitationOfType, UseInvitationOptions } from "simplydo/interfaces";

import {
  acceptAuthenticatedInvitation,
  rejectAuthenticatedInvitation,
  removeAuthenticatedInvitation,
  toastError,
  toastMessage,
  toastSuccess,
  withInvitationPrompt,
} from "./internal";
import type { InvitationViewModel } from "./types";
import { emptyInvitationViewModel } from "./types";

export const useIdeaAssessorInvitation = (
  invitation: InvitationOfType<"ideaAssessor"> | undefined,
  options?: UseInvitationOptions,
): InvitationViewModel => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const challenge = invitation?.challenge;
  const challengeId = challenge?._id;
  const challengeName = challenge?.name || t("invitations.deletedChallenge");
  const forIdeasCount = invitation?.forIdeas?.length ?? 0;
  const inviterName = invitation?.inviterUser?.profile?.fullName;
  const inviteeName =
    invitation?.inviteeUser?.profile?.fullName || invitation?.invitee;
  const canAccept = !!invitation?.canAccept && !!challenge;
  const createdByCurrentUser = !!invitation?.createdByCurrentUser;

  const acceptInvite = useCallback(() => {
    if (!invitation) {
      return;
    }
    acceptAuthenticatedInvitation(
      invitation,
      () => {
        navigate(`/challenges/${challengeId}/assessments/assigned`);
        toastSuccess(
          t("invitations.ideaAssessor.accepted", { idea: t("generic.idea") }),
        );
      },
      () =>
        toastError(
          t("invitations.ideaAssessor.acceptError", {
            idea: t("generic.idea"),
          }),
        ),
    );
  }, [challengeId, invitation, navigate, t]);

  const rejectInvite = useCallback(() => {
    if (!invitation) {
      return;
    }
    withInvitationPrompt(
      options,
      t("invitations.ideaAssessor.rejectTitle"),
      t("invitations.ideaAssessor.rejectMessage", {
        inviterName,
        ideas: pluralise(forIdeasCount, t("generic.idea"), t("generic.ideas")),
        challengeName,
        challenge: t("generic.challenge"),
      }),
      () => {
        rejectAuthenticatedInvitation(
          invitation,
          () => {
            toastMessage(t("invitations.rejected"));
            navigate(`/`);
          },
          () => toastError(t("invitations.rejectError")),
        );
      },
    );
  }, [
    challengeName,
    forIdeasCount,
    invitation,
    inviterName,
    navigate,
    options,
    t,
  ]);

  const removeInvite = useCallback(() => {
    if (!invitation) {
      return;
    }
    withInvitationPrompt(
      options,
      t("invitations.ideaAssessor.removeTitle"),
      t("invitations.ideaAssessor.removeMessage", {
        inviteeName,
        ideas: t("generic.ideas"),
        challengeName,
        challenge: t("generic.challenge"),
      }),
      () => {
        removeAuthenticatedInvitation(
          invitation,
          () => {
            toastMessage(t("invitations.removed"));
            navigate(`/challenges/${challengeId}/assessments`);
          },
          () => toastError(t("invitations.removeError")),
        );
      },
    );
  }, [
    challengeId,
    challengeName,
    invitation,
    inviteeName,
    navigate,
    options,
    t,
  ]);

  return useMemo(() => {
    if (!invitation) {
      return emptyInvitationViewModel();
    }

    return {
      icon: LightbulbIcon,
      link: challengeId ? `/challenges/${challengeId}` : null,
      isInvalid: !challenge,
      text: {
        title: challengeName,
        fullTitle: t("invitations.ideaAssessor.fullPageTitle", {
          name: challengeName,
          challenge: t("generic.challenge"),
        }),
        youInvited: t("invitations.ideaAssessor.creatorPostText", {
          inviteeName,
          ideas: pluralise(
            forIdeasCount,
            t("generic.idea"),
            t("generic.ideas"),
          ),
          challenge: t("generic.challenge"),
        }),
        hasInvitedYou: t("invitations.ideaAssessor.receivedPostText", {
          ideas: pluralise(
            forIdeasCount,
            t("generic.idea"),
            t("generic.ideas"),
          ),
          challenge: t("generic.challenge"),
          ideasLabel: pluralise(
            forIdeasCount,
            t("generic.idea"),
            t("generic.ideas"),
            false,
          ),
          challengeName,
        }),
        dismiss: createdByCurrentUser
          ? t("invitations.ideaAssessor.deleteInvitation")
          : t("invitations.reject"),
        invalid: t("invitations.ideaAssessor.invalidMessage", {
          challenge: t("generic.challenge"),
        }),
        ...(challengeId ? { goTo: t("invitations.ideaAssessor.view") } : {}),
      },
      ...(createdByCurrentUser ? { showAccept: false } : {}),
      acceptInvite: canAccept ? acceptInvite : undefined,
      rejectInvite: !createdByCurrentUser ? rejectInvite : undefined,
      removeInvite: createdByCurrentUser ? removeInvite : undefined,
    };
  }, [
    acceptInvite,
    canAccept,
    challenge,
    challengeId,
    challengeName,
    createdByCurrentUser,
    forIdeasCount,
    invitation,
    inviteeName,
    removeInvite,
    rejectInvite,
    t,
  ]);
};
